/**
 * Управление качеством видеопотоков на основе количества участников и режима комнаты
 */

import { useEffect, useMemo, useRef } from 'react';
import { RemoteParticipant, RemoteTrack, RemoteTrackPublication, RoomEvent, Track } from 'livekit-client';
import { applyVideoQuality, getRoomMode } from 'utils/videoQualityHelpers';
import { selectRoom } from 'state/selectors/room';
import {
    selectFirstScreenSharingParticipant,
    selectIsScreenShared,
    selectParticipants,
    selectRecentSpeakerId,
} from 'state/selectors/participants';
import { ROOM_MODE, STAGE_LAYOUT, StageLayout } from '../consts';

interface VideoQualityOptions {
    isSidebarMode?: boolean;
    stageLayout?: StageLayout; // Явно передаем stageLayout из компонента, который знает свой режим
}

export const useVideoQualityByParticipantCount = ({
    isSidebarMode = false,
    stageLayout = STAGE_LAYOUT.GRID, // По умолчанию GRID
}: VideoQualityOptions = {}) => {
    const room = selectRoom();
    const participants = selectParticipants();
    const isScreenSharing = selectIsScreenShared();
    // В режиме screen share используем участника, который шарит экран, иначе - последнего говорившего
    const screenShareParticipant = selectFirstScreenSharingParticipant();
    const recentSpeakerId = selectRecentSpeakerId();
    const speakerParticipantId = isScreenSharing
        ? screenShareParticipant?.identity ?? undefined
        : recentSpeakerId ?? undefined;

    const localParticipant = room?.localParticipant;
    // Используем переданный stageLayout напрямую - компонент знает свой режим
    const mode = useMemo(
        () => getRoomMode(isScreenSharing, speakerParticipantId, stageLayout),
        [isScreenSharing, speakerParticipantId, stageLayout],
    );
    const totalParticipants = participants.length;
    const previousSpeakerRef = useRef<string | undefined>(undefined);
    const previousModeRef = useRef<typeof mode | undefined>(undefined);

    useEffect(() => {
        if (!room || !localParticipant) return;

        const context = { mode, totalParticipants, speakerParticipantId, isSidebarMode };
        const modeChanged = previousModeRef.current !== undefined && previousModeRef.current !== mode;

        // Применяем качество к локальному участнику
        applyVideoQuality(localParticipant, context);

        // Применяем качество ко всем удаленным участникам с подпиской на камеру
        // Это работает для всех режимов (GRID, SCREEN_SHARE, SPEAKER)
        participants.forEach((participant) => {
            if (participant.isLocal) return;

            // Оптимизация: используем getTrackPublication вместо итерации по всем публикациям
            const cameraPublication = participant.getTrackPublication(Track.Source.Camera);
            if (cameraPublication?.isSubscribed) {
                applyVideoQuality(participant, context);
            }
        });

        // Обработка смены спикера или режима: понижаем качество предыдущего спикера
        const previousSpeakerId = previousSpeakerRef.current;
        if ((previousSpeakerId && previousSpeakerId !== speakerParticipantId) || modeChanged) {
            // Если режим изменился на GRID, нужно понизить качество всем предыдущим спикерам
            if (modeChanged && mode === ROOM_MODE.GRID && previousSpeakerId) {
                const previousParticipant = participants.find((p) => p.identity === previousSpeakerId && !p.isLocal);
                if (previousParticipant) {
                    // Применяем качество без учета спикера (теперь он не спикер в grid режиме)
                    applyVideoQuality(previousParticipant, { ...context, speakerParticipantId: undefined });
                }
            } else if (previousSpeakerId && previousSpeakerId !== speakerParticipantId) {
                const previousParticipant = participants.find((p) => p.identity === previousSpeakerId && !p.isLocal);
                if (previousParticipant) {
                    // Применяем качество без учета спикера (теперь он не спикер)
                    applyVideoQuality(previousParticipant, { ...context, speakerParticipantId: undefined });
                }
            }
        }

        previousSpeakerRef.current = speakerParticipantId;
        previousModeRef.current = mode;
    }, [room, localParticipant, mode, totalParticipants, participants, speakerParticipantId, isSidebarMode]);

    useEffect(() => {
        if (!room) return;

        const handleTrackSubscribed = (
            _remoteTrack: RemoteTrack,
            remoteTrackPublication: RemoteTrackPublication,
            remoteParticipant: RemoteParticipant,
        ) => {
            if (remoteTrackPublication.source !== Track.Source.Camera || !remoteTrackPublication.isSubscribed) {
                return;
            }

            const totalParticipants = room.remoteParticipants.size + 1;
            const context = { mode, totalParticipants, speakerParticipantId, isSidebarMode };

            // Применяем качество по режиму (GRID, SCREEN_SHARE, SPEAKER)
            // В GRID режиме спикер не приоритизируется, в SPEAKER/SCREEN_SHARE - получает HIGH
            applyVideoQuality(remoteParticipant, context);
        };

        room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        return () => {
            room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        };
    }, [room, mode, speakerParticipantId, isSidebarMode]);
};
